export default {
  "slug": "battery-standby-calculation",
  "title": "Fire Alarm Battery (Standby) Calculation Basics",
  "description": "How fire alarm standby battery sizing works: standby vs alarm current, the required standby and alarm durations, and why a safety derating factor is added.",
  "keywords": "fire alarm battery calculation, standby battery, secondary power, battery backup sizing, fire alarm low battery trouble signal, battery trouble troubleshooting, NFPA 72 secondary power",
  "ogImage": "/guide-og/battery-standby-calculation.jpg",
  "faqs": [
    {
      "q": "How long must fire alarm batteries last?",
      "a": "Batteries must carry the system through a required standby period (often measured in hours) and still have capacity to sound the alarm for the required alarm time. The exact durations depend on the system type and adopted code edition."
    },
    {
      "q": "Why is a safety factor added to battery sizing?",
      "a": "A derating/safety factor (commonly around 20%) is added so that as batteries age and lose capacity, they still meet the required standby and alarm performance."
    },
    {
      "q": "What happens if the batteries are undersized?",
      "a": "Undersized batteries may not carry the system through a power outage or sound the alarm long enough, leaving the building unprotected when normal power is lost."
    }
  ],
  "category": "Systems",
  "readMins": 5,
  "published": "2026-09-27",
  "updated": "2026-09-27",
  "hero": "/guide-hero/battery-standby-calculation.jpg",
  "heroAlt": "Illustration of a fire alarm control panel connected to two backup batteries",
  "excerpt": "Secondary batteries keep a fire alarm running when the power fails. Here's how standby battery sizing works, in plain language.",
  "sections": [
    {
      "h": "Why batteries are sized",
      "p": "Secondary (standby) batteries keep the system running when normal AC power is lost. They must carry the panel through a required standby period and still have capacity to sound the alarm."
    },
    {
      "h": "Standby vs alarm current",
      "p": "You total the current the system draws in normal supervisory (standby) mode and the higher current it draws in alarm. Standby typically runs for hours; the alarm period runs for minutes."
    },
    {
      "h": "The calculation, in concept",
      "p": "Battery capacity (amp-hours) must cover (standby current × standby hours) + (alarm current × alarm time); then a derating/safety factor — commonly about 20% — is added so aging batteries still perform."
    },
    {
      "h": "Low-battery trouble signals and troubleshooting",
      "p": "When secondary power weakens, the panel annunciates a battery/low-battery trouble — a supervisory condition, not an alarm. Common causes are batteries past their service life (typically a few years), a failed charger, loose or corroded terminals, or an undersized set that can't hold the required standby. Troubleshoot by checking terminal voltage under load, confirming the charger output, inspecting connections, and verifying the battery date. Replace batteries in matched pairs and re-test so the system regains its full standby and alarm capacity."
    },
    {
      "h": "Verify the numbers",
      "p": "Required standby and alarm durations depend on the system type and the adopted code edition. Always confirm the exact hours and safety factor for your specific project."
    }
  ],
  "relatedModules": [
    "control-panels",
    "testing-maintenance",
    "wiring-circuitry"
  ],
  "relatedGuides": [
    "fire-alarm-wiring-and-circuits",
    "voltage-drop-calculation",
    "commercial-fire-alarm-troubleshooting",
    "preparing-for-a-fire-alarm-inspection"
  ]
};
